import { useQuery } from "@tanstack/react-query";
import axios from "axios";

/**
 * Shipping-provider registry + per-store shipping accounts (master admin).
 *
 * Backend endpoints (shipping-providers integration — built in parallel):
 *   GET /api/shipping/carriers                    → carrier registry
 *   GET /api/stores/{storeId}/shipping-accounts   → accounts for one store
 *
 * Carrier shape: { slug, name, supports_api, environments: ['uat','production'],
 *                  credential_fields: [{key,label,secret}], default_tracking_url_template }
 * Account shape: { id, store_id_m, carrier, carrier_name, label, environment,
 *                  supports_api, is_enabled, has_credentials, connection_status,
 *                  connected_at, tracking_url_template }
 */
export const useShippingCarriers = ({ enabled = true } = {}) => {
  const query = useQuery({
    queryKey: ["shipping-carriers"],
    queryFn: async () => {
      const { data } = await axios.get("/api/shipping/carriers");
      return Array.isArray(data) ? data : [];
    },
    enabled,
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  return {
    carriers: query.data || [],
    status: query.status,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useShippingAccounts = (storeIdM, { enabled = true } = {}) => {
  const query = useQuery({
    queryKey: ["shipping-accounts", storeIdM],
    queryFn: async () => {
      const { data } = await axios.get(
        `/api/stores/${storeIdM}/shipping-accounts`
      );
      return Array.isArray(data) ? data : [];
    },
    enabled: enabled && !!storeIdM,
    staleTime: 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  return {
    accounts: query.data || [],
    status: query.status,
    error: query.error,
    refetch: query.refetch,
  };
};
